import { graphql, PageProps } from "gatsby";
import React, { FC, useContext } from "react";
import { GetGeneralContentQuery } from "../../types/graphql-types";
import { ExtendedPortableText } from "../components/ExtendedPortableText";
import { PageIntroProps } from "../components/PageIntro";
import { VideoBlock } from "../components/VideoBlock";
import { PageContext } from "../context/PageContext";
import { useUrlMap } from "../hooks/useUrlMap";
import urlFor from "../utilities/images/imageBuilder";
import { Article } from "./layouts/Article";
import { SanitySiteLayout } from "./layouts/SanitySiteLayout";

const SanityGeneralContentTemplate: FC<PageProps<GetGeneralContentQuery>> = ({
  data
}) => {
  const pageContext = useContext(PageContext);
  const urlMap = useUrlMap();

  if (!data.currentPage) {
    return null;
  }

  const { currentPage } = data;

  const breadcrumbs = pageContext.parentNodes?.map(parentNode => ({
    url: urlMap[parentNode._id ?? ""] ?? "/",
    title: parentNode.title ?? "",
    label: parentNode.title ?? "",
    isLast: false
  }));

  breadcrumbs?.push({
    label: currentPage.title ?? "",
    title: currentPage.title ?? "",
    url: "",
    isLast: true
  });

  const tagNames = currentPage.tags?.map(
    x => x?.title && x.title[pageContext.language.replace("-", "_")]
  );

  const generalContentHeader: PageIntroProps = {
    heading: currentPage.title ?? "",
    preamble: currentPage.preamble ?? "",
    breadcrumbs: breadcrumbs ?? [],
    imageUrl:
      (currentPage.image?.asset &&
        urlFor(currentPage.image).height(1500).width(2000).url()) ??
      "",
    tags: tagNames
  };

  return (
    <SanitySiteLayout>
      <Article
        articleHeader={generalContentHeader}
        videoBlock={<VideoBlock video={currentPage.videoBlocks?.[0]} />}
      >
        <ExtendedPortableText value={currentPage._rawMainBody} />
      </Article>
    </SanitySiteLayout>
  );
};

export { Head } from "../components/Head";

export const query = graphql`
  query GetGeneralContent($_id: String!) {
    currentPage: sanityGeneralContent(_id: { eq: $_id }) {
      title
      preamble
      _updatedAt
      language
      _id
      slug {
        current
      }
      image {
        ...SanityExtendedImageFragment
      }
      _rawMainBody(resolveReferences: { maxDepth: 10 })
      tags {
        ...SanityTagFragment
      }
      videoBlocks {
        videoId
        videoText
      }
    }
  }
`;

export default SanityGeneralContentTemplate;
